import { useEffect, useRef, useState } from 'react';
import { emailValidator } from 'darwin_core/validators.js';

import { useAuth } from './AuthenticationContext.jsx';
import {
  offerPasskeyAutofill,
  passkeyCancelled,
  passkeysSupported,
} from '../passkeys/webauthn.js';
import HaloLoginDevices from '../components/HaloLoginDevices.jsx';
import LegalFooter from '../legal/LegalFooter.jsx';
import PasswordField from '../components/PasswordField.jsx';
import TextField from '../components/TextField.jsx';

// Logging in on one form, as in the app: the email, the password and a passkey
// together. Error copy mirrors the mobile app's SignIn
// (app/src/screens/Auth/SignIn.js), which is the reference for every string on
// this screen. The app shows these in an alert dialog; here they render inline
// under the form, which is the web convention and what the fields'
// aria-describedby points at.

// Bad credentials. The app deliberately gives the same message whether the
// account exists or not, so a sign-in attempt can't be used to enumerate emails.
const INCORRECT_CREDENTIALS = 'Incorrect username or password.';

// Everything that isn't one of the recognised cases maps to this single generic
// message, so a raw Cognito exception (which can carry internal detail) is never
// rendered to the user.
const GENERIC_ERROR = 'Please check your account details, or try again later.';

// Cognito raises its exceptions by `name`; the two "next step" cases are re-thrown
// by our auth context as `new Error(code)` (code in `message`), so match on both.
function messageForError(error) {
  const codes = [error && error.name, error && error.message];
  if (
    codes.includes('NotAuthorizedException') ||
    codes.includes('UserNotFoundException')
  ) {
    return INCORRECT_CREDENTIALS;
  }
  if (codes.includes('PasswordResetRequiredException')) {
    // The app sends the user to its in-app code entry here. That needs a code,
    // which only the reset email brings, so point at the reset flow that sends
    // one.
    return 'Your password needs to be reset. Use “Forgot Password?” to set a new one.';
  }
  if (codes.includes('UserNotConfirmedException')) {
    // The app navigates to its email-confirmation screen; web can't, so say where
    // to finish.
    return 'Your account isn’t confirmed yet. Open the Halo mobile app to finish confirming it, then sign in here.';
  }
  return GENERIC_ERROR;
}

// The user closed the browser's prompt, or the browser had no passkey for Halo:
// browsers report both the same way on purpose.
const PASSKEY_NOT_USED =
  'No passkey was used. Try again, or sign in with your password.';

// The passkey wasn't accepted: the account is gone, can't sign in, or doesn't
// have that passkey any more. One message for them all, as the app gives
// (its notAccepted), naming the usual cause: a reset removes the account's
// passkeys, but the password manager still offers them.
const PASSKEY_NOT_ACCEPTED =
  'Your passkey wasn’t accepted. Please try again, or sign in with your email and password. If you’ve reset your password, your passkeys were removed: sign in with your email and password, then add a passkey again.';

// Signing in with a passkey takes the account from it, and this one didn't
// say. Only the password can sign in without that.
const PASSKEY_WITHOUT_ACCOUNT =
  'Your passkey didn’t say which Halo account it’s for, so it can’t sign you in. Please sign in with your email and password.';

// The API's challenge couldn't be had, so no passkey was asked for: no answer
// at all, or one turning it away (passkeys off, or busy). The app says the
// same (its unreachable and failed).
const PASSKEY_API_UNREACHABLE =
  'Couldn’t reach Halo. Check your connection and try again.';
const PASSKEY_API_FAILED =
  'Passkeys couldn’t be used just now. Please try again, or sign in with your email and password.';

function messageForPasskeyError(error) {
  if (passkeyCancelled(error)) {
    return PASSKEY_NOT_USED;
  }
  const codes = [error && error.name, error && error.message];
  if (codes.includes('PasskeyApiUnreachable')) {
    return PASSKEY_API_UNREACHABLE;
  }
  if (codes.includes('PasskeyApiFailed')) {
    return PASSKEY_API_FAILED;
  }
  if (codes.includes('PasskeyWithoutUserHandle')) {
    return PASSKEY_WITHOUT_ACCOUNT;
  }
  if (
    codes.includes('PasskeyNotAccepted') ||
    codes.includes('NotAuthorizedException') ||
    codes.includes('UserNotFoundException') ||
    codes.includes('PasswordResetRequiredException') ||
    codes.includes('UserNotConfirmedException')
  ) {
    return PASSKEY_NOT_ACCEPTED;
  }
  return GENERIC_ERROR;
}

// What's signed in with: the email as typed, trimmed and lower-cased. The
// field itself is left as typed, so a password manager's fill isn't seen as
// edited.
const normalised = (email) => email.trim().toLowerCase();

export default function SignIn({
  onForgotPassword,
  autoFocusHeading = false,
  initialEmail = '',
}) {
  const { signIn, signInWithDiscoverablePasskey } = useAuth();
  const [passkeysWork] = useState(passkeysSupported);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  const headingRef = useRef(null);

  // Only steal focus when this view is re-shown by a toggle (back from
  // forgot-password), not on the initial gate render.
  useEffect(() => {
    if (autoFocusHeading) {
      headingRef.current?.focus();
    }
  }, [autoFocusHeading]);

  // One sign-in at a time, password or passkey. A second sign-in replaces the
  // first's Cognito session, so a passkey's correct answer would be refused.
  // While one runs, the buttons are marked unavailable (aria-disabled) and the
  // fields read-only rather than disabled, so the one in use keeps focus and a
  // keyboard user carries on from it after a failure; this is what makes them
  // do nothing. A ref, as `pending` only shows once React re-renders: a fast
  // second click, or Enter in a field, could start another first.
  const signingIn = useRef(false);

  // The email field's autofill offers this browser's Halo passkeys too, where
  // the browser can (see offerPasskeyAutofill), for as long as the form is on
  // screen. The browser has one passkey request pending at a time, so the
  // offer is stopped before the passkey button opens the prompt, and while a
  // password sign-in runs, so a passkey picked late can't start a second
  // sign-in over it. It's made again (bumping `autofillRound`) after a sign-in
  // fails.
  const stopAutofill = useRef(() => {});
  const [autofillRound, setAutofillRound] = useState(0);
  const offerAutofillAgain = () => setAutofillRound((round) => round + 1);

  // One passkey sign-in, if none is under way (see signingIn). Success
  // re-gates the app and unmounts this form, as a password sign-in does, so
  // only a failure needs a state update.
  const passkeySignIn = async (signInWith) => {
    if (signingIn.current) {
      return;
    }
    setError(null);
    signingIn.current = true;
    setPending(true);
    try {
      const session = await signInWith();
      if (session) {
        return;
      }
      setError(GENERIC_ERROR);
      setPending(false);
      offerAutofillAgain();
    } catch (ex) {
      setError(messageForPasskeyError(ex));
      setPending(false);
      offerAutofillAgain();
    } finally {
      signingIn.current = false;
    }
  };

  useEffect(() => {
    if (!passkeysWork) {
      return undefined;
    }
    const stop = offerPasskeyAutofill((answer) =>
      passkeySignIn(() => signInWithDiscoverablePasskey(answer)),
    );
    stopAutofill.current = stop;
    return stop;
    // passkeySignIn is made afresh each render, but only reads refs and sets
    // state, so the first one's closure does as well as any.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passkeysWork, autofillRound, signInWithDiscoverablePasskey]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (signingIn.current) {
      return;
    }
    signingIn.current = true;
    stopAutofill.current();
    setError(null);
    const address = normalised(email);
    try {
      const validation = await emailValidator(address);
      if (!validation.valid) {
        setError(validation.error);
        offerAutofillAgain();
        return;
      }
      setPending(true);
      const session = await signIn(address, password);
      if (session) {
        // A real success flips `authenticated`, which re-gates the app and
        // unmounts this form, so no state update is needed on that path. The
        // fields aren't cleared: a password manager offers to save the login
        // when the form leaves the screen still filled in.
        return;
      }
      // signIn resolved but no session materialised (no tokens / a race). The
      // gate won't unmount us, so surface feedback rather than leaving the form
      // stuck disabled with no explanation.
      setError(GENERIC_ERROR);
      setPending(false);
      offerAutofillAgain();
    } catch (ex) {
      setError(messageForError(ex));
      setPending(false);
      offerAutofillAgain();
    } finally {
      signingIn.current = false;
    }
  };

  // The browser's prompt offers any of its Halo passkeys, and the one picked
  // names the account: no email needed.
  const handlePasskey = () => {
    if (signingIn.current) {
      return;
    }
    stopAutofill.current();
    passkeySignIn(() => signInWithDiscoverablePasskey());
  };

  // Layout and copy follow the app's SignIn: the devices illustration, the green
  // "Welcome to Halo," title, placeholder-labelled Email and Password fields with
  // "Forgot Password?" tucked under the password, then Sign in, then Sign in
  // with a passkey where the browser has passkeys. The app also offers "Create
  // an account" and "Try demo"; both are out of scope for web. Below the form
  // sits the legal footer: the app carries those two links on its sign-up
  // screen, which web does not have (see legal/LegalFooter.jsx).
  // The fields are TextFields: placeholder-labelled like the app, with a hidden
  // <label> for assistive tech (see components/TextField.jsx for the rationale).
  // The email is the login's username, as on the reset screen, so a password
  // manager files both under the same account, and it's marked "webauthn" too,
  // so the browser can offer passkeys in its autofill.
  return (
    <main className="app-shell auth-screen">
      <div className="auth-brand">
        <HaloLoginDevices />
      </div>
      <h1
        id="main-content"
        className="auth-screen__title"
        ref={headingRef}
        tabIndex={-1}
      >
        Welcome to Halo,
      </h1>
      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <TextField
          id="signin-email"
          name="email"
          type="email"
          label="Email"
          autoComplete="username webauthn"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          readOnly={pending}
          required
          errorId={error ? 'signin-error' : undefined}
        />

        <PasswordField
          id="signin-password"
          name="password"
          label="Password"
          className="text-field--tight"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          readOnly={pending}
          required
          errorId={error ? 'signin-error' : undefined}
        />

        {/* type="button" so Enter in a field still submits the form (Sign in
            is the form's default), and the link can never submit. It takes
            the email typed so far, so it needn't be typed again. */}
        <button
          type="button"
          className="link-button auth-form__link"
          onClick={() => onForgotPassword?.(email.trim())}
        >
          Forgot Password?
        </button>

        <button
          type="submit"
          className="auth-form__submit"
          aria-disabled={pending ? 'true' : undefined}
        >
          {pending ? 'Signing in…' : 'Sign in'}
        </button>

        {/* Wherever the browser can use passkeys: the browser says itself when
            it has none for the site, and can offer a phone nearby. It needs no
            email: the passkey picked names its account. type="button" so Enter
            in a field still submits the password form. */}
        {passkeysWork ? (
          <button
            type="button"
            className="btn-secondary auth-form__passkey"
            onClick={handlePasskey}
            aria-disabled={pending ? 'true' : undefined}
          >
            Sign in with a passkey
          </button>
        ) : null}

        {/* Always present so assistive tech has the live region in the tree
            before an error is set; role=alert announces it when populated. The id
            lets the fields point their aria-describedby at it while it holds an
            error (the fields' errorId above). Collapses to nothing while empty. */}
        <p
          id="signin-error"
          className="form-error"
          role="alert"
          aria-live="assertive"
        >
          {error}
        </p>
      </form>
      <LegalFooter />
    </main>
  );
}
